import React, { useEffect, useState, useCallback } from 'react'
import { apiGet, apiPut, getErrorMessage } from '../../utils/api'
import { useNotifications } from '../../contexts/NotificationContext'
import {
  PageContainer,
  PageHeader,
  SectionCard,
  PrimaryButton,
  OutlineButton,
  IconButton,
  FormField,
  TextInput,
  TextArea,
  Select,
  Checkbox,
  PageSpinner,
  ErrorBanner,
} from '../ui'

type QuickActionType = 'department' | 'url' | 'start_chat'

interface QuickAction {
  label: string
  action: QuickActionType
  value: string
}

interface WidgetConfig {
  primary_color: string
  header_title: string
  header_subtitle: string
  greeting: string
  quick_actions: QuickAction[]
  show_busy_agents: boolean
  use_agent_avatars: boolean
  features?: { cobrowse?: boolean } | null
  updated_at?: string | null
  updated_by?: string | null
}

const DEFAULT_ACTION: QuickAction = { label: '', action: 'department', value: '' }

/* ── Small icons used only on this page ───────────────────────────────── */

const PaintIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 110-18 9 9 0 010 18zm0 0v-4.5m0 0a3 3 0 100-6 3 3 0 000 6z" />
  </svg>
)
const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
)
const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
)
const ChevronUp = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
  </svg>
)
const ChevronDown = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
)

/* ── Page ──────────────────────────────────────────────────────────────── */

export function WidgetSettingsPage() {
  const { addNotification } = useNotifications()
  const [config, setConfig] = useState<WidgetConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await apiGet<WidgetConfig>('/widget-config')
      setConfig(data)
      setError(null)
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load widget config'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const update = <K extends keyof WidgetConfig>(key: K, value: WidgetConfig[K]) => {
    setConfig((c) => c ? { ...c, [key]: value } : c)
  }

  const updateAction = (i: number, patch: Partial<QuickAction>) => {
    setConfig((c) => {
      if (!c) return c
      const next = [...c.quick_actions]
      next[i] = { ...next[i], ...patch }
      return { ...c, quick_actions: next }
    })
  }

  const addAction = () => {
    setConfig((c) => c ? { ...c, quick_actions: [...c.quick_actions, { ...DEFAULT_ACTION }] } : c)
  }

  const removeAction = (i: number) => {
    setConfig((c) => {
      if (!c) return c
      return { ...c, quick_actions: c.quick_actions.filter((_, idx) => idx !== i) }
    })
  }

  const moveAction = (i: number, delta: number) => {
    setConfig((c) => {
      if (!c) return c
      const next = [...c.quick_actions]
      const j = i + delta
      if (j < 0 || j >= next.length) return c
      ;[next[i], next[j]] = [next[j], next[i]]
      return { ...c, quick_actions: next }
    })
  }

  const save = async () => {
    if (!config) return
    for (const a of config.quick_actions) {
      if (!a.label.trim()) {
        addNotification('Every quick action needs a label', 'error')
        return
      }
      if (a.action === 'url') {
        try { new URL(a.value) }
        catch { addNotification(`Invalid URL for "${a.label}"`, 'error'); return }
      }
    }

    setSaving(true)
    try {
      await apiPut('/widget-config', {
        header_title: config.header_title,
        header_subtitle: config.header_subtitle,
        greeting: config.greeting,
        quick_actions: config.quick_actions,
        show_busy_agents: config.show_busy_agents,
        use_agent_avatars: config.use_agent_avatars,
        // Preserve any other feature flags already stored; only touch cobrowse.
        features: { ...(config.features || {}), cobrowse: !!config.features?.cobrowse },
      })
      addNotification('Widget settings saved', 'success')
      load()
    } catch (e) {
      addNotification(getErrorMessage(e, 'Save failed'), 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <PageContainer><PageSpinner /></PageContainer>
  if (error) return <PageContainer><ErrorBanner message={error} /></PageContainer>
  if (!config) return null

  return (
    <PageContainer>
      <PageHeader
        title="Widget Settings"
        subtitle="Customize how the chat widget appears on your website"
        action={
          <PrimaryButton icon={<PaintIcon />} loading={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save Changes'}
          </PrimaryButton>
        }
      />

      {/* ── Appearance ──────────────────────────────────────────────── */}
      <SectionCard
        title="Appearance"
        description="Avatar and agent-status behaviour"
      >
        <div className="space-y-3">
          <Checkbox
              label="Use agents' real profile photos"
              description="Falls back to coloured initials when an agent hasn't uploaded an avatar"
              checked={config.use_agent_avatars}
              onChange={(e) => update('use_agent_avatars', e.target.checked)}
            />
            <Checkbox
              label='Show "X busy" count'
              description='Appears in the agents-online strip, e.g. "2 agents online, 1 busy"'
              checked={config.show_busy_agents}
              onChange={(e) => update('show_busy_agents', e.target.checked)}
            />
        </div>
      </SectionCard>

      {/* ── Live features ───────────────────────────────────────────── */}
      <SectionCard
        title="Live features"
        description="Optional tools agents can use during a live chat"
      >
        <Checkbox
          label="Co-browse (screen sharing)"
          description="Lets an agent request to view the visitor's page during a chat. View-only, and only after the visitor consents each time. Requires the server co-browse feature to be enabled."
          checked={!!config.features?.cobrowse}
          onChange={(e) => update('features', { ...(config.features || {}), cobrowse: e.target.checked })}
        />
      </SectionCard>

      {/* ── Text content ────────────────────────────────────────────── */}
      <SectionCard
        title="Text content"
        description="What visitors read in the widget header and welcome panel"
      >
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <FormField label="Header title">
              <TextInput
                value={config.header_title}
                onChange={(e) => update('header_title', e.target.value)}
                maxLength={100}
              />
            </FormField>
            <FormField label="Header subtitle">
              <TextInput
                value={config.header_subtitle}
                onChange={(e) => update('header_subtitle', e.target.value)}
                maxLength={255}
              />
            </FormField>
          </div>
          <FormField label="Welcome greeting" help="Line breaks are preserved. Emoji welcome.">
            <TextArea
              rows={3}
              value={config.greeting}
              onChange={(e) => update('greeting', e.target.value)}
              placeholder={'Welcome to WeQuote 👋\nHow can we help?'}
            />
          </FormField>
        </div>
      </SectionCard>

      {/* ── Quick action bubbles ────────────────────────────────────── */}
      <SectionCard
        title="Quick action bubbles"
        description="Clickable shortcuts shown below the greeting"
        padded={false}
        headerAction={
          <OutlineButton icon={<PlusIcon />} onClick={addAction}>
            Add bubble
          </OutlineButton>
        }
      >
        {config.quick_actions.length === 0 ? (
          <div className="p-8 text-center text-sm text-txt-tertiary italic">
            No bubbles configured. Visitors will see the greeting only.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {config.quick_actions.map((a, i) => (
              <div key={i} className="p-5 hover:bg-surface-hover/40 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="flex flex-col gap-1 pt-7">
                    <IconButton
                      icon={<ChevronUp />}
                      onClick={() => moveAction(i, -1)}
                      disabled={i === 0}
                      aria-label="Move up"
                      className="p-1.5"
                    />
                    <IconButton
                      icon={<ChevronDown />}
                      onClick={() => moveAction(i, +1)}
                      disabled={i === config.quick_actions.length - 1}
                      aria-label="Move down"
                      className="p-1.5"
                    />
                  </div>

                  <div className="flex-1 grid md:grid-cols-[1fr_180px_2fr] gap-3">
                    <FormField label="Label" size="sm">
                      <TextInput
                        value={a.label}
                        onChange={(e) => updateAction(i, { label: e.target.value })}
                        placeholder="Sales"
                      />
                    </FormField>
                    <FormField label="Action" size="sm">
                      <Select
                        value={a.action}
                        onChange={(e) => updateAction(i, { action: e.target.value as QuickActionType })}
                      >
                        <option value="department">Route to department</option>
                        <option value="url">Open URL</option>
                        <option value="start_chat">Start chat (pre-filled)</option>
                      </Select>
                    </FormField>
                    <FormField
                      label={a.action === 'url' ? 'Destination URL' : a.action === 'department' ? 'Department key' : 'Pre-filled message'}
                      size="sm"
                    >
                      <TextInput
                        value={a.value}
                        onChange={(e) => updateAction(i, { value: e.target.value })}
                        placeholder={a.action === 'url' ? 'https://…' : a.action === 'department' ? 'sales' : "I'm interested in..."}
                        className="font-mono text-xs"
                      />
                    </FormField>
                  </div>

                  <IconButton
                    icon={<TrashIcon />}
                    variant="danger"
                    onClick={() => removeAction(i)}
                    title="Remove bubble"
                    aria-label="Remove bubble"
                    className="mt-7"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {config.updated_at && (
        <p className="text-xs text-txt-tertiary text-right">
          Last updated {new Date(config.updated_at).toLocaleString()}
          {config.updated_by ? ` by ${config.updated_by}` : ''}
        </p>
      )}
    </PageContainer>
  )
}
